import React, { useState, useRef, useEffect } from "react";
import { Box, TextField, Paper, MenuItem } from "@mui/material";

const DropDownField = ({
  label,
  value,
  onChange,
  disabled,
  options = [],
  error,
  searchable = false,
  freeSolo = false,
  onAdd,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef(null);

  // Sync display text with selected option or value
  useEffect(() => {
    const selected = options.find((o) => o.guid === value);
    setInputValue(selected ? selected.name : value || "");
  }, [value, options]);

  // ✅ CLOSE ON OUTSIDE CLICK
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        // Reset input value to match selected option on blur if not freeSolo
        if (!freeSolo) {
          const selected = options.find((o) => o.guid === value);
          setInputValue(selected ? selected.name : "");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options, freeSolo]);

  const filteredOptions = searchable && inputValue
    ? options.filter((o) =>
        o.name.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (freeSolo && inputValue.trim()) {
        e.preventDefault();
        e.stopPropagation();

        const trimmed = inputValue.trim();
        const matched = options.find(
          (o) => o.name.toLowerCase() === trimmed.toLowerCase()
        );

        if (matched) {
          onChange(matched.guid);
        } else {
          if (onAdd) {
            onAdd(trimmed);
          } else {
            onChange(trimmed);
          }
        }
        setOpen(false);
      }
    }
  };

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", mb: 2 }}>
      <TextField
        label={label}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (!open) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        fullWidth
        disabled={disabled}
        error={error}
        InputProps={{
          sx: { borderRadius: 3, cursor: searchable ? "text" : "pointer" },
          readOnly: !searchable,
        }}
        onClick={(e) => {
          e.stopPropagation(); // 🔥 IMPORTANT
          setOpen(true);
        }}
      />

      {open && filteredOptions.length > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 7000, // higher than panel
            maxHeight: 250,
            overflowY: "auto",
            borderRadius: 2,
            mt: 0.5,
          }}
        >
          {filteredOptions.map((item) => (
            <MenuItem
              key={item.guid}
              disabled={item.disabled}
              onClick={() => {
                if (!item.disabled) {
                  onChange(item.guid);
                  setInputValue(item.name);
                  setOpen(false); // ✅ close after select
                }
              }}
              sx={
                item.disabled
                  ? { color: "text.disabled", bgcolor: "action.disabledBackground" }
                  : {}
              }
            >
              {item.name}
            </MenuItem>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default DropDownField;

